import type {
  SearchResult,
  SearchScope,
  SearchSuggestionGroups,
} from "types/search";
import type { SearchLocale } from "./searchCopy";

export const isSearchScope = (value: string | null | undefined): value is SearchScope =>
  value === "all" ||
  value === "issues" ||
  value === "posts" ||
  value === "comments" ||
  value === "actors" ||
  value === "tags";

export const getSearchLocale = (search: string): SearchLocale => {
  const params = new URLSearchParams(search);
  const locale = params.get("locale");

  if (locale === "ja-JP" || locale === "ko-KR" || locale === "en-US") {
    return locale;
  }

  const browserLocale =
    typeof navigator !== "undefined" ? navigator.language : "en-US";

  if (browserLocale.startsWith("ja")) {
    return "ja-JP";
  }

  if (browserLocale.startsWith("ko")) {
    return "ko-KR";
  }

  return "en-US";
};

export const flattenSuggestionGroups = (
  groups: SearchSuggestionGroups
): SearchResult[] => [
  ...groups.issues,
  ...groups.posts,
  ...groups.actors,
  ...groups.tags,
];

export const buildSearchRoute = (
  query: string,
  scope: SearchScope,
  locale: SearchLocale
): string => {
  const params = new URLSearchParams();
  const normalizedQuery = query.trim().replace(/\s+/g, " ");

  if (normalizedQuery) {
    params.set("q", normalizedQuery);
  }

  if (scope !== "all") {
    params.set("type", scope);
  }

  params.set("locale", locale);

  return `/search?${params.toString()}`;
};

export const buildSearchSectionRoute = (
  section: string,
  locale: SearchLocale
): string => {
  const params = new URLSearchParams();
  params.set("section", section);
  params.set("locale", locale);

  return `/search?${params.toString()}`;
};

export const appendLocaleToPath = (path: string, locale: SearchLocale): string => {
  const [pathname, search = ""] = path.split("?");
  const params = new URLSearchParams(search);
  params.set("locale", locale);
  const queryString = params.toString();

  return queryString.length > 0 ? `${pathname}?${queryString}` : pathname;
};

export const buildResultPath = (result: SearchResult, locale: SearchLocale): string =>
  appendLocaleToPath(result.routePath, locale);

export const stripHighlightMarkers = (value: string | null | undefined): string =>
  (value || "").replace(/\[\[hl\]\]/g, "").replace(/\[\[\/hl\]\]/g, "");

export const splitHighlightedFragments = (
  value: string | null | undefined
): Array<{ text: string; highlighted: boolean }> => {
  const input = value || "";

  if (!input) {
    return [];
  }

  const fragments: Array<{ text: string; highlighted: boolean }> = [];
  let remaining = input;

  while (remaining.length > 0) {
    const startIndex = remaining.indexOf("[[hl]]");

    if (startIndex < 0) {
      fragments.push({ text: remaining, highlighted: false });
      break;
    }

    if (startIndex > 0) {
      fragments.push({
        text: remaining.slice(0, startIndex),
        highlighted: false,
      });
    }

    const afterStart = remaining.slice(startIndex + "[[hl]]".length);
    const endIndex = afterStart.indexOf("[[/hl]]");

    if (endIndex < 0) {
      fragments.push({ text: afterStart, highlighted: true });
      break;
    }

    fragments.push({
      text: afterStart.slice(0, endIndex),
      highlighted: true,
    });

    remaining = afterStart.slice(endIndex + "[[/hl]]".length);
  }

  return fragments.filter((fragment) => fragment.text.length > 0);
};
